import { ChartDateRange, DimensionId, KpiId } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { toChartDateRange, toPickerRange } from '../Dashboard/date-range';

// The focused chart view's URL contract: the transient view state (date, comparison window, KPI,
// metric, breakdown) a chart is being LOOKED at through, as query params. Written by a dashboard
// card on expand so the focused view opens on exactly what the card was showing, and re-written by
// the focused view's own pickers so the view survives a reload and a copied link carries it.
//
// Nothing here is part of a chart's saved definition — an absent param means "use the chart's own
// saved value", which is what a chart opened cold from the library gets.
export const PARAM = {
  START: 'start',
  END: 'end',
  COMPARE_START: 'compareStart',
  COMPARE_END: 'compareEnd',
  KPI: 'kpi',
  METRIC: 'metric',
  BREAKDOWN: 'breakdown'
} as const;

export interface ChartViewParams {
  dateRange?: ChartDateRange;
  comparisonRange?: ChartDateRange;
  kpiId?: KpiId;
  metric?: string;
  breakdown?: DimensionId;
}

// ISO "YYYY-MM-DD" — the only date shape the picker and a ChartConfig date range use. Anything else
// in the URL is someone's hand-edit; drop it rather than compiling it into a query.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// A range goes into the URL as CONCRETE dates: a relative range ("last 1 year") is resolved the same
// way the picker resolved it for display, so the focused view opens on the window the card actually
// showed rather than re-resolving relative-to-today at some later moment.
const writeRange = (
  search: URLSearchParams,
  range: ChartDateRange | undefined,
  startKey: string,
  endKey: string
) => {
  if (!range) return;
  const { start, end } = toPickerRange(range);
  search.set(startKey, start);
  search.set(endKey, end);
};

const readRange = (
  search: URLSearchParams,
  startKey: string,
  endKey: string
): ChartDateRange | undefined => {
  const start = search.get(startKey);
  const end = search.get(endKey);
  if (!start || !end || !ISO_DATE.test(start) || !ISO_DATE.test(end)) return undefined;
  return toChartDateRange({ start, end });
};

// Serialize to a search string ("?start=…", or "" when there's nothing to carry) for navigate().
export const toChartViewSearch = (view: ChartViewParams): string => {
  const search = new URLSearchParams();
  writeRange(search, view.dateRange, PARAM.START, PARAM.END);
  writeRange(search, view.comparisonRange, PARAM.COMPARE_START, PARAM.COMPARE_END);
  if (view.kpiId) {
    search.set(PARAM.KPI, view.kpiId);
  }
  if (view.metric) {
    search.set(PARAM.METRIC, view.metric);
  }
  if (view.breakdown) {
    search.set(PARAM.BREAKDOWN, view.breakdown);
  }
  const query = search.toString();
  return query ? `?${query}` : '';
};

// Read whatever the URL carries. Every field is independently optional — a partial or hand-edited
// query yields the fields that parse and undefined for the rest.
export const fromChartViewSearch = (search: URLSearchParams): ChartViewParams => ({
  dateRange: readRange(search, PARAM.START, PARAM.END),
  comparisonRange: readRange(search, PARAM.COMPARE_START, PARAM.COMPARE_END),
  kpiId: search.get(PARAM.KPI) ?? undefined,
  metric: search.get(PARAM.METRIC) ?? undefined,
  breakdown: search.get(PARAM.BREAKDOWN) ?? undefined
});

// Merge a change into the CURRENT params, dropping the keys it clears, so writing one picker never
// discards another's param. Returns a URLSearchParams for setSearchParams.
export const withChartViewParams = (
  current: URLSearchParams,
  view: ChartViewParams
): URLSearchParams => {
  const next = new URLSearchParams(current);
  for (const key of Object.values(PARAM)) {
    next.delete(key);
  }
  const written = new URLSearchParams(toChartViewSearch(view).replace(/^\?/, ''));
  written.forEach((value, key) => next.set(key, value));
  return next;
};
